import { createOrganizationSchema, formatCnpj, type CompanyOwnerStatus } from "@cafegestor/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Field,
  Input,
  PageTitle,
  Select,
  Spinner,
  Table,
} from "@/components/ui";
import { apiFetch, errorMessage, newIdempotencyKey } from "@/lib/api";
import { OwnerInvitationPanel } from "./OwnerInvitationPanel";

type Org = {
  id: string;
  name: string;
  status: string;
  companies: {
    id: string;
    legalName: string;
    tradeName: string | null;
    cnpj: string;
    status: string;
    /** situação do proprietário (sem token: o link só aparece ao emitir) */
    owner: CompanyOwnerStatus;
  }[];
};
type Created = {
  companyId: string;
  ownerMode: "self" | "invite";
  delivery: string | null;
  acceptUrl: string | null;
};

const empty = {
  organizationName: "",
  legalName: "",
  tradeName: "",
  cnpj: "",
  ownerMode: "invite",
  ownerEmail: "",
};

/**
 * Somente administradores da plataforma: criação de organizações e empresas (MVP) e
 * manutenção do convite do proprietário enquanto a empresa não tem proprietário ativo.
 */
export function PlatformPage() {
  const queryClient = useQueryClient();
  const orgs = useQuery({
    queryKey: ["plataforma", "organizacoes"],
    queryFn: () => apiFetch<Org[]>("/plataforma/organizacoes", { withCompany: false }),
  });
  const [form, setForm] = useState(empty);
  const [key, setKey] = useState(newIdempotencyKey);
  const [formError, setFormError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (body: unknown) =>
      apiFetch<Created>("/plataforma/organizacoes", {
        method: "POST",
        body,
        idempotencyKey: key,
        withCompany: false,
      }),
    onSuccess: async () => {
      setForm(empty);
      setKey(newIdempotencyKey());
      await queryClient.invalidateQueries({ queryKey: ["plataforma"] });
      await queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const parsed = createOrganizationSchema.safeParse({
      organizationName: form.organizationName,
      company: { legalName: form.legalName, tradeName: form.tradeName, cnpj: form.cnpj },
      owner:
        form.ownerMode === "self" ? { mode: "self" } : { mode: "invite", email: form.ownerEmail },
    });
    if (!parsed.success) return setFormError(parsed.error.issues[0]?.message ?? "Dados inválidos");
    setFormError(null);
    create.mutate(parsed.data);
  }

  const set = (name: keyof typeof empty) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [name]: e.target.value }));

  return (
    <div className="space-y-6">
      <PageTitle
        title="Plataforma"
        description="Organizações (clientes do SaaS) e suas empresas. No MVP, só a administração da plataforma cria empresas."
      />
      <Card title="Nova organização e empresa">
        <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
          <Field label="Nome da organização (cliente)" required>
            <Input value={form.organizationName} onChange={set("organizationName")} />
          </Field>
          <Field label="CNPJ da empresa" required>
            <Input value={form.cnpj} onChange={set("cnpj")} placeholder="00.000.000/0000-00" />
          </Field>
          <Field label="Razão social" required>
            <Input value={form.legalName} onChange={set("legalName")} />
          </Field>
          <Field label="Nome fantasia">
            <Input value={form.tradeName} onChange={set("tradeName")} />
          </Field>
          <Field label="Proprietário(a) da empresa">
            <Select value={form.ownerMode} onChange={set("ownerMode")}>
              <option value="invite">Convidar por e-mail</option>
              <option value="self">Eu mesma serei a proprietária</option>
            </Select>
          </Field>
          {form.ownerMode === "invite" && (
            <Field label="E-mail do(a) proprietário(a)" required>
              <Input type="email" value={form.ownerEmail} onChange={set("ownerEmail")} />
            </Field>
          )}
          <div className="space-y-3 sm:col-span-2">
            {formError && <Alert tone="error">{formError}</Alert>}
            {create.isError && <Alert tone="error">{errorMessage(create.error)}</Alert>}
            {create.data && (
              <Alert tone="ok">
                Empresa criada.
                {create.data.acceptUrl && (
                  <span className="mt-2 block">
                    Link do convite do(a) proprietário(a) (envie à pessoa):
                    <Input
                      className="mt-1"
                      readOnly
                      value={create.data.acceptUrl}
                      onFocus={(e) => e.target.select()}
                    />
                  </span>
                )}
              </Alert>
            )}
            <Button type="submit" disabled={create.isPending}>
              Criar
            </Button>
          </div>
        </form>
      </Card>

      <Card title="Organizações">
        {orgs.isPending ? (
          <Spinner />
        ) : orgs.isError ? (
          <Alert tone="error">{errorMessage(orgs.error)}</Alert>
        ) : (
          <Table head={["Organização", "Empresas e proprietário", "Situação"]}>
            {orgs.data.map((o) => (
              <tr key={o.id}>
                <td className="px-3 py-2 font-medium">{o.name}</td>
                <td className="px-3 py-2">
                  {o.companies.map((c) => (
                    <div key={c.id} className="py-1">
                      {c.tradeName ?? c.legalName}{" "}
                      <span className="text-muted-foreground">{formatCnpj(c.cnpj)}</span>
                      <OwnerInvitationPanel companyId={c.id} owner={c.owner} />
                    </div>
                  ))}
                </td>
                <td className="px-3 py-2">
                  <Badge tone={o.status === "active" ? "ok" : "warn"}>
                    {o.status === "active" ? "Ativa" : o.status}
                  </Badge>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </div>
  );
}
